/**
 * OpenSky Network flight tracking data source.
 * Fetches live aircraft positions via ADS-B receivers.
 * Free without auth (10 req/min), optional auth for higher limits (4000 req/day).
 */

import type { Source, SourceMeta, ConfigField, SourceStatus, NormalizedEvent } from '../base-source';
import { getApiBase } from '@/app/api-router';
import { enrichAircraft } from './wingbits-source';

const SOURCE_ID = 'opensky';

/**
 * OpenSky state vector indices:
 * 0: icao24, 1: callsign, 2: origin_country, 3: time_position,
 * 4: last_contact, 5: longitude, 6: latitude, 7: baro_altitude,
 * 8: on_ground, 9: velocity, 10: true_track, 11: vertical_rate,
 * 12: sensors, 13: geo_altitude, 14: squawk, 15: spi, 16: position_source, 17: category
 */
type StateVector = [
  string,        // 0 icao24
  string | null, // 1 callsign
  string,        // 2 origin_country
  number | null, // 3 time_position
  number,        // 4 last_contact
  number | null, // 5 longitude
  number | null, // 6 latitude
  number | null, // 7 baro_altitude
  boolean,       // 8 on_ground
  number | null, // 9 velocity
  number | null, // 10 true_track
  number | null, // 11 vertical_rate
  number[] | null, // 12 sensors
  number | null, // 13 geo_altitude
  string | null, // 14 squawk
  boolean,       // 15 spi
  number,        // 16 position_source
  number,        // 17 category
];

interface OpenSkyResponse {
  time: number;
  states: StateVector[] | null;
}

let status: SourceStatus = {
  state: 'initializing',
  eventCount: 0,
};

export const openskySource: Source = {
  id: SOURCE_ID,

  meta: {
    name: 'OpenSky Network',
    description: 'Live aircraft positions via ADS-B receivers',
    icon: 'plane',
    category: 'aviation',
    updateInterval: 30_000, // 30 sec — balances freshness vs pipeline load
    requiresApiKey: false,
    apiKeyUrl: 'https://opensky-network.org/index.php?option=com_users&view=registration',
    dataLicense: 'CC BY-NC 4.0',
    builtIn: true,
  } satisfies SourceMeta,

  configSchema: [
    { key: 'opensky_username', label: 'Username', type: 'text', required: false, helpText: 'Optional — enables higher API rate limits' },
    { key: 'opensky_password', label: 'Password', type: 'password', required: false },
  ] as ConfigField[],

  async initialize(): Promise<void> {
    status = { state: 'initializing', eventCount: 0 };
  },

  async start(): Promise<void> {
    status.state = 'active';
    console.log('[Sentinel] OpenSky flight source started');
  },

  async stop(): Promise<void> {
    status.state = 'disabled';
  },

  async fetch(): Promise<NormalizedEvent[]> {
    try {
      // Proxy handles optional Basic Auth from server-side env vars
      const url = `${getApiBase()}/api/data/opensky`;

      const response = await fetch(url, { signal: AbortSignal.timeout(15_000) });

      if (!response.ok) {
        throw new Error(`OpenSky API returned ${response.status}`);
      }

      const data: OpenSkyResponse = await response.json();

      if (!data.states || !Array.isArray(data.states)) {
        return [];
      }

      const events: NormalizedEvent[] = [];

      for (const state of data.states) {
        const icao24 = state[0];
        const callsign = state[1]?.trim() || null;
        const originCountry = state[2];
        const lng = state[5];
        const lat = state[6];
        const onGround = state[8];
        const velocity = state[9];
        const heading = state[10];
        const verticalRate = state[11];
        const geoAltitude = state[13];
        const squawk = state[14];

        // Skip aircraft without position
        if (lat == null || lng == null) continue;

        // Epoch-bucketed ID: position updates must pass through pipeline dedup.
        // Bucket to the update interval (30s) so each fetch cycle generates new IDs.
        const epochBucket = Math.floor(data.time / 30);
        events.push({
          id: `opensky-${icao24}-${epochBucket}`,
          source: SOURCE_ID,
          timestamp: new Date(data.time * 1000).toISOString(),
          type: 'flight_position',
          title: `${callsign || icao24} — ${originCountry}`,
          severity: 'info',
          location: {
            lat,
            lng,
            altitude: geoAltitude ?? undefined,
          },
          tags: ['aviation', originCountry, onGround ? 'grounded' : 'airborne'],
          raw: {
            icao24,
            callsign,
            velocity,
            heading,
            verticalRate,
            onGround,
            squawk,
            geoAltitude,
            // Feet-named at the boundary: geo_altitude is meters, the map's
            // altitude color stops are indexed in feet.
            altitudeFt: geoAltitude != null ? geoAltitude * 3.28084 : undefined,
          },
        });
      }

      // Cap at 3000 aircraft: prioritize airborne, then callsign-identified, then by altitude
      const MAX_EVENTS = 3000;
      if (events.length > MAX_EVENTS) {
        events.sort((a, b) => {
          const aGrounded = a.tags?.includes('grounded') ? 1 : 0;
          const bGrounded = b.tags?.includes('grounded') ? 1 : 0;
          if (aGrounded !== bGrounded) return aGrounded - bGrounded; // airborne first
          const aRaw = a.raw as Record<string, unknown> | undefined;
          const bRaw = b.raw as Record<string, unknown> | undefined;
          const aHasCall = aRaw?.callsign ? 0 : 1;
          const bHasCall = bRaw?.callsign ? 0 : 1;
          if (aHasCall !== bHasCall) return aHasCall - bHasCall; // callsign first
          return ((bRaw?.geoAltitude as number) ?? 0) - ((aRaw?.geoAltitude as number) ?? 0); // higher altitude first
        });
        events.length = MAX_EVENTS;
      }

      // Enrich with aircraft metadata (non-blocking — failures don't break the source)
      try {
        const icaoCodes = events.map(e => (e.raw as Record<string, unknown>)?.icao24 as string).filter(Boolean);
        const metaMap = await enrichAircraft(icaoCodes);

        for (const event of events) {
          const raw = event.raw as Record<string, unknown> | undefined;
          const icao = raw?.icao24 as string | undefined;
          if (!icao) continue;
          const meta = metaMap.get(icao);
          if (meta) {
            raw!.aircraft = meta;
            if (meta.isMilitary && !event.tags?.includes('military')) {
              event.tags = [...(event.tags ?? []), 'military'];
            }
          }
        }
      } catch {
        // enrichment is best-effort
      }

      status.state = 'active';
      status.lastFetch = new Date().toISOString();
      status.eventCount = events.length;
      return events;
    } catch (err) {
      status.state = 'error';
      status.lastError = err instanceof Error ? err.message : String(err);
      throw err;
    }
  },

  getStatus(): SourceStatus {
    return { ...status };
  },
};
